import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, ArrowRight, Sparkles, Gift, Ticket } from "lucide-react";
import { API } from "@/lib/auth";
import { COMPANY } from "@/lib/company";

/**
 * Generic post-purchase landing page. Routes:
 *   /portal-activated   → Stripe success URL for $485 portal
 *   /test-drive-thanks  → Stripe success URL for $9.95 test drive
 */
export default function PostPurchasePage({ product, title, subtitle, badge, nextCta }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // Derive product from path if not passed
  const resolved = product || (location.pathname.includes("test-drive") ? "test_drive_995" : "portal_485");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/voucher/capture`, {
        email: email.trim().toLowerCase(),
        full_name: name.trim() || null,
        product: resolved,
      });
      toast.success("Got it! Your confirmation is on the way.");
      setDone(true);
    } catch (err) {
      toast.error("Capture failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 bg-[#020617] aios-starfield" data-testid="post-purchase-page">
      <div className="absolute inset-0 aios-grid-overlay opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-2xl">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-red-600/15 border border-red-600/40 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-red-500" />
          </div>
          <span className="mt-6 text-red-500 font-display text-xs tracking-[0.3em] font-bold">
            {badge}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-4 tracking-tighter">
            {title}
          </h1>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
            {subtitle}
          </p>
        </div>

        {done ? (
          <div className="mt-10 aios-card p-8 text-center" data-testid="capture-success">
            <div className="aios-card-accent" />
            <Sparkles className="w-8 h-8 text-red-500 mx-auto" />
            <h2 className="font-display text-2xl font-bold text-white mt-4 tracking-tight">
              You&apos;re on the list.
            </h2>
            <p className="text-slate-400 mt-3 text-sm">
              Check your inbox over the next few minutes. We&apos;ll send delivery instructions to <span className="text-white font-semibold">{email}</span>.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="aios-btn-secondary text-xs">
                Back to Home
              </Link>
              {nextCta && (
                <button onClick={() => navigate(nextCta.to)} className="aios-btn-primary text-xs">
                  {nextCta.label} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 aios-card p-8 space-y-4" data-testid="capture-form">
            <div className="aios-card-accent" />
            <h2 className="font-display text-xl font-bold text-white tracking-tight">
              Where should we deliver your confirmation?
            </h2>
            <p className="text-slate-400 text-sm">
              Enter the email Stripe charged or any email you&apos;d like the voucher / portal instructions sent to.
            </p>
            <input
              type="text"
              placeholder="Full name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="capture-input-name"
              className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
            />
            <input
              type="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="capture-input-email"
              className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
            />
            <button type="submit" disabled={busy} data-testid="capture-submit" className="aios-btn-primary w-full text-sm">
              <Mail className="w-4 h-4" />
              {busy ? "Sending..." : "Send My Confirmation"}
            </button>
            <p className="text-xs text-slate-500 text-center pt-2">
              Questions about your purchase? Email{" "}
              <a href={COMPANY.emailLink} className="text-red-500 hover:underline">{COMPANY.email}</a>
              {" "}or call{" "}
              <a href={COMPANY.phoneLink} className="text-red-500 hover:underline">{COMPANY.phone}</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export function PortalActivatedPage() {
  return (
    <PostPurchasePage
      product="portal_485"
      badge="PORTAL ACTIVATED"
      title="Your $485 Corporate Portal is Active."
      subtitle="Welcome to the A.I.O sourcing desk. Your portal license, voucher generator, and sourcing concierge are unlocked. Confirm the email below and we'll send your portal access details immediately."
      nextCta={{ label: "Submit your first sourcing request", to: "/contact" }}
    />
  );
}

export function TestDriveThanksPage() {
  return (
    <PostPurchasePage
      product="test_drive_995"
      badge="TEST DRIVE UNLOCKED"
      title="Your $9.95 Voucher is Reserved."
      subtitle="Enter the email Stripe charged below. Your 3-Night Luxury Hotel Stay voucher confirmation will land in your inbox within a few minutes — no timeshare nonsense, no contract, no catch."
      nextCta={{ label: "Ready for the full $485 portal?", to: "/activate" }}
    />
  );
}
